/**
 * FO-406 (batch-51) - Finqle merchant-API (F1, retrieve-only).
 *
 * Statussen zijn 1-op-1 overgenomen uit de Finqle-integratiegids; er worden geen
 * eigen namen verzonnen. De NL-labels leven uitsluitend in de UI-laag (deze
 * constanten), zodat de opgeslagen waarde altijd de Finqle-waarde blijft.
 *
 * Pure module: DTO's, statussen, labels en Zod-schemas. Geen Prisma/I/O.
 */

import { z } from 'zod'
import type { WerkbonGoedkeuringStatus, WerkbonStatus, WerkbonType } from './werkbonnen'
// batch-63 facturen-bronregel (FO-470) — herkomst + werkvoorraad van een verzoek.
import {
  FINQLE_VERZOEK_BRONNEN,
  FINQLE_VERZOEK_WERKVOORRADEN,
  type FinqleVerzoekBronKey,
  type FinqleVerzoekFactuurStand,
} from './facturen-bronregel'

// ─── Billing request ─────────────────────────────────────────────────────────

/**
 * Lifecycle van een facturatieverzoek (guide): DRAFT -> SUBMITTED ->
 * APPROVED/DISAPPROVED -> INVOICED. `revertBillingRequest` kan een DISAPPROVED
 * verzoek legitiem terugzetten naar DRAFT; dat is de enige toegestane stap
 * terug (de sync volgt hem, met audit).
 */
export const FINQLE_BILLING_REQUEST_STATUSSEN = [
  'DRAFT',
  'SUBMITTED',
  'APPROVED',
  'DISAPPROVED',
  'INVOICED',
] as const

export type FinqleBillingRequestStatusKey = (typeof FINQLE_BILLING_REQUEST_STATUSSEN)[number]

/** NL-labels voor de status-pipeline in de facturen-werkruimte. */
export const FINQLE_BILLING_REQUEST_STATUS_LABELS: Record<FinqleBillingRequestStatusKey, string> = {
  DRAFT: 'Concept',
  SUBMITTED: 'Wacht op akkoord klant',
  APPROVED: 'Akkoord',
  DISAPPROVED: 'Afgekeurd',
  INVOICED: 'Gefactureerd',
}

/**
 * Badge-kleur per status - één waarheid voor lijst én detail (varianten van de
 * `@streep/ui-fp`-Badge; die kent geen `destructive`/`outline`).
 */
export const FINQLE_BILLING_REQUEST_STATUS_BADGE: Record<
  FinqleBillingRequestStatusKey,
  'default' | 'warning' | 'success' | 'danger' | 'primary'
> = {
  DRAFT: 'default',
  SUBMITTED: 'warning',
  APPROVED: 'success',
  DISAPPROVED: 'danger',
  INVOICED: 'primary',
}

/**
 * Volgorde in de pipeline. Bepaalt of een status-overgang "vooruit" is: de sync
 * accepteert alleen vooruit, plus de expliciete revert DISAPPROVED -> DRAFT.
 */
export const FINQLE_BILLING_REQUEST_STATUS_VOLGORDE: Record<FinqleBillingRequestStatusKey, number> =
  {
    DRAFT: 0,
    SUBMITTED: 1,
    APPROVED: 2,
    DISAPPROVED: 2,
    INVOICED: 3,
  }

/**
 * Mag de sync `van` -> `naar` toepassen? Vooruit mag altijd; gelijk blijven mag
 * (idempotente herhaling); terug alleen voor de gedocumenteerde revert
 * DISAPPROVED -> DRAFT. Elke andere stap terug is een out-of-order antwoord
 * (bv. een trage pagina) en wordt genegeerd zodat de spiegel niet terugvalt.
 */
export function isToegestaneBillingRequestOvergang(
  van: FinqleBillingRequestStatusKey,
  naar: FinqleBillingRequestStatusKey,
): boolean {
  if (van === naar) return true
  if (van === 'DISAPPROVED' && naar === 'DRAFT') return true
  return FINQLE_BILLING_REQUEST_STATUS_VOLGORDE[naar] > FINQLE_BILLING_REQUEST_STATUS_VOLGORDE[van]
}

// ─── Finqle-factuur ──────────────────────────────────────────────────────────

/**
 * Lifecycle van een Finqle-factuur (guide): CREATED -> SUBMITTED ->
 * PENDING_DEBTOR_APPROVAL -> APPROVED/DISAPPROVED -> TO_BE_PAID -> OPEN ->
 * FINALIZED/CLOSED. Wordt als tekst gespiegeld (de app leidt er zijn eigen
 * factuurstatus niet uit af).
 */
export const FINQLE_INVOICE_STATUSSEN = [
  'CREATED',
  'SUBMITTED',
  'PENDING_DEBTOR_APPROVAL',
  'PENDING_SIGN_OFF',
  'APPROVED',
  'DISAPPROVED',
  'ON_HOLD',
  'TO_BE_PAID',
  'OPEN',
  'FINALIZED',
  'CLOSED',
] as const

export type FinqleInvoiceStatusKey = (typeof FINQLE_INVOICE_STATUSSEN)[number]

export const FINQLE_INVOICE_STATUS_LABELS: Record<FinqleInvoiceStatusKey, string> = {
  CREATED: 'Aangemaakt',
  SUBMITTED: 'Ingediend',
  PENDING_DEBTOR_APPROVAL: 'Wacht op akkoord klant',
  // Live-enum 2026-08-14: het echte InvoiceStatusEnum kent ook deze twee.
  PENDING_SIGN_OFF: 'Wacht op aftekening',
  APPROVED: 'Akkoord',
  DISAPPROVED: 'Afgekeurd',
  ON_HOLD: 'Aangehouden',
  TO_BE_PAID: 'Te betalen',
  OPEN: 'Openstaand',
  FINALIZED: 'Afgerond',
  CLOSED: 'Gesloten',
}

// ─── Webhooks ────────────────────────────────────────────────────────────────

/**
 * De events waarop STREEP zich abonneert. Registratie gebeurt door FINQLE zelf
 * (de merchant/v1-API kent geen webhook-mutatie - live geverifieerd 2026-08-14);
 * deze lijst is wat wij per account-URL doorgeven. Collectieve-factuur- en
 * statement-events kunnen later bij zonder codewijziging aan de ontvangkant.
 */
export const FINQLE_WEBHOOK_EVENTS = [
  'BILLING_REQUESTS_INVOICED',
  'INVOICE_PAID_BY_DEBTOR',
  'INVOICE_PAID_TO_MERCHANT',
] as const

export type FinqleWebhookEvent = (typeof FINQLE_WEBHOOK_EVENTS)[number]

/**
 * Payload-vorm van een Finqle-webhook: `{ resource_type, action, object }`.
 * De webhook is een TRIGGER, geen waarheid: uit `object` gebruiken we alleen de
 * id om de resource daarna via de API na te fetchen.
 */
export const finqleWebhookPayloadSchema = z.object({
  resource_type: z.string().min(1),
  action: z.string().min(1),
  object: z
    .object({
      id: z.union([z.string(), z.number()]).optional(),
      identifier: z.string().optional(),
    })
    .passthrough(),
})

export type FinqleWebhookPayload = z.infer<typeof finqleWebhookPayloadSchema>

/**
 * Bron-IP's van Finqle (guide). Config-constante; het daadwerkelijk openzetten
 * van de allowlist is infra-werk (CC.6). Sandbox en productie staan bewust
 * apart zodat een sandbox-bron nooit productie-data raakt.
 */
export const FINQLE_WEBHOOK_IPS = {
  productie: ['159.223.243.217', '134.209.137.83'],
  sandbox: ['64.225.80.137'],
} as const

// ─── Merchant-accounts (multi-account) ───────────────────────────────────────

/**
 * De Finqle merchant-accounts van STREEP: één per facturerende BV, elk met een
 * eigen API-key (`FINQLE_API_KEY_<SLUG>` in env, uit Key Vault) en een eigen
 * webhook-pad (`/webhooks/finqle/<slug>`). Welke BV bij welk account hoort staat
 * in `Entity.finqleApiAccount` (beheerbaar op instellingen -> entiteiten).
 *
 * Bewust een VASTE lijst: een typefout in env of DB kan zo nooit stil een
 * "nieuw account" worden. Een account erbij (bv. groep) = waarde hier toevoegen
 * plus de env-key en het BV-veld - één wijziging, zichtbaar in review.
 */
export const FINQLE_ACCOUNTS = ['service', 'onderhoud', 'personeel', 'projecten'] as const

export type FinqleAccountSlug = (typeof FINQLE_ACCOUNTS)[number]

/** NL-labels voor beheer-UI en meldingen. */
export const FINQLE_ACCOUNT_LABELS: Record<FinqleAccountSlug, string> = {
  service: 'Service',
  onderhoud: 'Onderhoud',
  personeel: 'Personeel',
  projecten: 'Projecten',
}

export function isFinqleAccountSlug(waarde: string): waarde is FinqleAccountSlug {
  return (FINQLE_ACCOUNTS as readonly string[]).includes(waarde)
}

/** Env-var-naam van de API-key van een account (Key Vault: `finqle-api-key-<slug>`). */
export function finqleApiKeyEnvNaam(account: FinqleAccountSlug): string {
  return `FINQLE_API_KEY_${account.toUpperCase()}`
}

// ─── Spiegel-DTO's (lijst + detail) ──────────────────────────────────────────

/**
 * Hoe een werkbon aan een verzoek hangt: automatisch (matcher), handmatig, of
 * bij het versturen van de werkbon naar Finqle (batch-63, lokaal verzoek).
 */
export type FinqleKoppelBronKey = 'MATCHER' | 'HANDMATIG' | 'VERZENDING'

export const FINQLE_KOPPEL_BRON_LABELS: Record<FinqleKoppelBronKey, string> = {
  MATCHER: 'Automatisch gekoppeld',
  HANDMATIG: 'Handmatig gekoppeld',
  VERZENDING: 'Gekoppeld bij verzending',
}

/** Eén gekoppelde werkbon bij een facturatieverzoek. */
export interface FinqleBillingRequestWerkbonDTO {
  werkbonId: string
  werkbonNumber: string
  /** Hoe de koppeling is gelegd. */
  koppelBron: FinqleKoppelBronKey
}

/** Rij in de lijst "Facturatieverzoeken (Finqle)". */
export interface FinqleBillingRequestDTO {
  id: string
  /**
   * Finqle-UUID; null voor een lokaal verzoek (`bron = STREEP_VERZENDING`) dat
   * Finqle nog niet via de API heeft gemeld.
   */
  finqleId: string | null
  /** batch-63 (FO-470) — herkomst van het verzoek. */
  bron: FinqleVerzoekBronKey
  status: FinqleBillingRequestStatusKey
  statusLabel: string
  clientId: string | null
  clientNaam: string | null
  entityId: string | null
  entityNaam: string | null
  /** Bedragen als string (Decimal), nooit als float. */
  bedragExclBtw: string | null
  bedragInclBtw: string | null
  btwBedrag: string | null
  valuta: string
  projectName: string | null
  poNumber: string | null
  referentie: string | null
  submittedOn: string | null
  approvedOn: string | null
  disapprovalReason: string | null
  /** Ouderdom in dagen sinds indienen (Finqle `daysOpen` of zelf afgeleid). */
  daysOpen: number | null
  werkbonnen: FinqleBillingRequestWerkbonDTO[]
  /**
   * Waarom er (nog) geen werkbon automatisch gekoppeld is (matcher-reden of
   * BV-mismatch); null zodra er gekoppeld is. Zichtbaar in de werkvoorraad.
   */
  redenGeenMatch: string | null
  /** Gekoppelde factuurrij (mailbox/upload), via de koppelaar. */
  invoiceId: string | null
  invoiceNummer: string | null
  finqleInvoiceId: string | null
  finqleInvoiceStatus: FinqleInvoiceStatusKey | null
  /** batch-63 (FO-470) — fiscaal Finqle-factuurnummer zodra bekend. */
  finqleInvoiceNumber: string | null
  /** batch-63 (FO-470) — gebundeld in een Finqle-factuur op (ISO). */
  invoicedAt: string | null
  /** batch-63 (FO-470) — klant betaalde aan Finqle op (ISO). */
  paidByDebtorAt: string | null
  /** batch-63 (FO-470) — Finqle betaalde aan STREEP uit op (ISO). */
  paidAt: string | null
  /** batch-63 (FO-470) — ontvangen, nog niet ontvangen, of nog niet verwacht. */
  factuurStand: FinqleVerzoekFactuurStand
  laatstGesyncedOp: string | null
}

// ─── Detail-DTO (verzoek-detailpagina) ───────────────────────────────────────

/** Bron van een mutatie in de audit-trail (spiegel van de Prisma-enum). */
export const FINQLE_MUTATIE_BRONNEN = ['SYNC', 'WEBHOOK', 'HANDMATIG'] as const

export type FinqleMutatieBronKey = (typeof FINQLE_MUTATIE_BRONNEN)[number]

export const FINQLE_MUTATIE_BRON_LABELS: Record<FinqleMutatieBronKey, string> = {
  SYNC: 'Automatische sync',
  WEBHOOK: 'Finqle-webhook',
  HANDMATIG: 'Handmatig',
}

/**
 * NL-labels voor de audit-acties (schrijfplekken: `spiegel.ts` + de
 * koppel-routes). Bewust een open lookup mét fallback in de UI: een nieuwe
 * actie-sleutel mag de detailpagina nooit breken.
 */
export const FINQLE_AUDIT_ACTIE_LABELS: Record<string, string> = {
  AANGEMAAKT: 'Verzoek aangemaakt',
  STATUS: 'Status gewijzigd',
  TERUGGEZET: 'Teruggezet naar concept',
  GEKOPPELD: 'Werkbon gekoppeld',
  ONTKOPPELD: 'Werkbon ontkoppeld',
  FACTUUR_GEKOPPELD: 'Factuur gekoppeld',
  // batch-60 facturen-verwijderen (FO-443) - spiegel-rij verwijderd/hersteld.
  FACTUUR_ONTKOPPELD: 'Factuur ontkoppeld (verwijderd)',
  FACTUUR_HERSTELD: 'Factuur hersteld en opnieuw gekoppeld',
  // batch-63 facturen-bronregel (FO-470) - lokaal verzoek + koppelaar + opschoning.
  VERZONDEN: 'Werkbon naar Finqle verzonden',
  OPNIEUW_VERZONDEN: 'Werkbon opnieuw naar Finqle verzonden',
  GEADOPTEERD: 'Gekoppeld aan het Finqle-verzoek',
  VERVANGEN: 'Vervangen door het Finqle-verzoek',
  FACTUURNUMMER: 'Finqle-factuurnummer bekend',
  BETAALD: 'Betaalstand bijgewerkt',
  UIT_PROFORMA: 'Overgenomen uit de interne proforma (opschoning)',
  UIT_SPIEGEL: 'Overgenomen van de gespiegelde factuurrij (opschoning)',
  // batch-65 finqle-verzamelbon (FO-484) - één verzoek per verzamelbon.
  VERVANGEN_DOOR_VERZAMELBON: 'Vervangen: de werkbonnen zijn opnieuw verzonden in een verzamelbon',
  BONNEN_OPNIEUW_VERZONDEN: 'Een deel van de werkbonnen is opnieuw verzonden in een verzamelbon',
  AFGEKEURD_DOOR_KLANT: 'Afgekeurd door de klant in het portaal (laat het bij Finqle afkeuren)',
  DEELS_AFGEKEURD_DOOR_KLANT: 'Deels afgekeurd door de klant in het portaal (laat het bij Finqle afkeuren)',
  BON_GECREDITEERD: 'Werkbon uit de verzamelbon gecrediteerd (verzoek blijft)',
  BON_LOS_OPNIEUW_VERZONDEN: 'Werkbon uit de verzamelbon los opnieuw verzonden (corrigeer bij Finqle)',
}

/** Eén regel in de audit-tijdlijn op de detailpagina. */
export interface FinqleBillingRequestAuditDTO {
  id: string
  /** Actie-sleutel; label via `FINQLE_AUDIT_ACTIE_LABELS` (met fallback). */
  actie: string
  vanStatus: FinqleBillingRequestStatusKey | null
  naarStatus: FinqleBillingRequestStatusKey | null
  bron: FinqleMutatieBronKey
  /** Naam van de gebruiker bij een handmatige actie; null voor sync/webhook. */
  performedByNaam: string | null
  op: string
  metadata: Record<string, unknown> | null
}

/** Uitgebreide werkbon-informatie bij een gekoppelde werkbon (detailpagina). */
export interface FinqleVerzoekWerkbonDetailDTO {
  werkbonId: string
  werkbonNumber: string
  displayName: string | null
  status: WerkbonStatus
  goedkeuringStatus: WerkbonGoedkeuringStatus | null
  type: WerkbonType
  /** Locatienaam + plaats, bv. "Hoofdmagazijn - Bergen"; null zonder locatie. */
  locatie: string | null
  /** Uitvoeringsperiode: eerste en laatste dag-entry (ISO), of null zonder. */
  periodeVan: string | null
  periodeTot: string | null
  /** Namen van de monteurs met gewerkte (niet-afwezige) dagen, gededupliceerd. */
  monteurs: string[]
  /** Som van de gewerkte uren (`hours`, waar de overuren al in zitten). */
  totaalUren: string
  /** Waarvan overuren (deelverzameling van `totaalUren`), als Decimal-string. */
  totaalOveruren: string
  /** Aantal gewerkte KALENDERdagen (dag-entries zijn dag x monteur). */
  dagen: number
  koppelBron: FinqleKoppelBronKey
  gekoppeldOp: string
  /** Wie handmatig koppelde; null bij de automatische matcher. */
  gekoppeldDoorNaam: string | null
  /** FO-008: is dit een besloten werkbon? */
  besloten: boolean
  /**
   * FO-008: inhoud afgeschermd voor deze viewer? Zo ja, dan zijn displayName,
   * locatie en monteurs geneutraliseerd; aantallen/status/periode blijven.
   */
  afgeschermd: boolean
}

/**
 * Detail van één facturatieverzoek: de lijst-DTO plus herkomst, de Finqle-
 * relatiegegevens uit de gespiegelde payload, verrijkte werkbon-informatie en
 * de audit-tijdlijn. De payload-velden zijn defensief geparsed (kunnen bij een
 * oude rij ontbreken) en dus allemaal nullable.
 */
export interface FinqleBillingRequestDetailDTO extends FinqleBillingRequestDTO {
  finqleAccount: FinqleAccountSlug | null
  finqleAccountLabel: string | null
  debtorBranchId: string | null
  /** Debiteurnaam zoals Finqle hem kent (payload `relations.issuedToName`). */
  finqleDebiteurNaam: string | null
  /** Merchant-/issuernaam bij Finqle (payload `relations.issuerName`). */
  finqleIssuerNaam: string | null
  /** Regelomschrijvingen uit het verzoek (uren-/unit-/kostenregels). */
  regelOmschrijvingen: string[]
  /** Wanneer Finqle het verzoek aanmaakte (payload `created`), of null. */
  finqleAangemaaktOp: string | null
  /** Wanneer het verzoek in een factuur is gebundeld (payload `invoicedOn`). */
  invoicedOn: string | null
  /** Wanneer STREEP het verzoek voor het eerst spiegelde (createdAt). */
  eersteSyncOp: string
  /** F2-voorbereiding: door STREEP zelf opgemaakt? In F1 altijd false. */
  aangemaaktDoorStreep: boolean
  werkbonDetails: FinqleVerzoekWerkbonDetailDTO[]
  audits: FinqleBillingRequestAuditDTO[]
}

/**
 * Sorteerbare kolommen van de verzoeken-lijst. Server-side orderBy (de lijst is
 * server-gepagineerd, dus client-side sorteren zou alleen de zichtbare pagina
 * sorteren); de UI-kolomkoppen en de API-allowlist delen deze constante.
 */
export const FINQLE_BILLING_REQUEST_SORT_VELDEN = [
  'klant',
  'bv',
  'status',
  'bedrag',
  'ouderdom',
  'ingediend',
] as const

export type FinqleBillingRequestSortVeld = (typeof FINQLE_BILLING_REQUEST_SORT_VELDEN)[number]

/** Filters op de facturatieverzoeken-lijst. */
export const finqleBillingRequestListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  status: z.enum(FINQLE_BILLING_REQUEST_STATUSSEN).optional(),
  clientId: z.string().min(1).optional(),
  entityId: z.string().min(1).optional(),
  /** Merchant-account-herkomst (multi-account); voedt de beheer-doorklik. */
  account: z.enum(FINQLE_ACCOUNTS).optional(),
  /** `true` = alleen verzoeken zonder gekoppelde werkbon ("niet gekoppeld"). */
  nietGekoppeld: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => v === true || v === 'true')
    .optional(),
  zoek: z.string().trim().min(1).max(200).optional(),
  /** Zonder sortField geldt de bestaande default (ingediend nieuw -> oud). */
  sortField: z.enum(FINQLE_BILLING_REQUEST_SORT_VELDEN).optional(),
  sortDir: z.enum(['asc', 'desc']).optional(),
  /**
   * batch-63 (FO-470) — werkvoorraad: `zonderFactuur` = verzonden
   * (SUBMITTED/APPROVED/INVOICED) zonder gekoppelde factuurrij.
   */
  werkvoorraad: z.enum(FINQLE_VERZOEK_WERKVOORRADEN).optional(),
  /** batch-63 (FO-470) — herkomst: gespiegeld of lokaal (verzending). */
  bron: z.enum(FINQLE_VERZOEK_BRONNEN).optional(),
})

export type FinqleBillingRequestListQuery = z.infer<typeof finqleBillingRequestListQuerySchema>

/** Handmatig een werkbon aan een verzoek koppelen of ontkoppelen (T10). */
export const finqleBillingRequestKoppelSchema = z.object({
  werkbonId: z.string().min(1),
})

export type FinqleBillingRequestKoppelInput = z.infer<typeof finqleBillingRequestKoppelSchema>

/**
 * batch-63 facturen-bronregel (FO-470) — een geboekte factuur handmatig aan een
 * verzoek koppelen (als de koppelaar twijfelde of niets vond).
 */
export const finqleBillingRequestFactuurKoppelSchema = z.object({
  invoiceId: z.string({ error: 'Kies een factuur' }).min(1, 'Kies een factuur'),
  /**
   * batch-63 (review 5 B1, review 6) — bevestigd: de factuur met dit id (uit de
   * 409 `VRIJGEVEN_BEVESTIGEN`) voert deze Finqle-factuur nog zonder
   * facturatieverzoek en geeft de koppeling vrij. Een ander id telt niet.
   */
  vrijgeven: z.string().min(1).optional(),
})

/**
 * batch-63 — wat er met de betaalstand van een factuur gebeurt die een
 * Finqle-koppeling loslaat: de automatische betaald-markering gaat terug, alleen
 * een automatisch aangevulde betaaldatum gaat weg, een betaalstand zonder
 * automatisch spoor (oude sync, handmatig) blijft staan, of er was niets.
 */
export type FinqleBetaalstandNaLoslaten = 'TERUGGEZET' | 'DATUM_VERWIJDERD' | 'BLIJFT_BETAALD' | 'ONGEWIJZIGD'

/** batch-63 — de factuur die een Finqle-koppeling (na bevestiging) vrijgeeft. */
export interface FinqleFactuurVrijgave {
  invoiceId: string
  factuurnummer: string | null
  betaalstand: FinqleBetaalstandNaLoslaten
}

/** batch-63 — antwoord van het handmatig koppelen. */
export interface FinqleBillingRequestFactuurKoppelResultaat {
  gekoppeld: true
  /** De factuur die de Finqle-koppeling (na bevestiging) heeft vrijgegeven. */
  vrijgegeven: FinqleFactuurVrijgave | null
  /** Tekst voor de melding na het koppelen. */
  melding: string
}

/** batch-63 — details bij 409 `VRIJGEVEN_BEVESTIGEN`. */
export interface FinqleFactuurVrijgevenDetails {
  vrijgeven: FinqleFactuurVrijgave
}

/** batch-63 (review 6) — antwoord van het losmaken van de factuurkoppeling. */
export interface FinqleBillingRequestFactuurOntkoppelResultaat {
  ontkoppeld: true
  /** Tekst voor de melding, met wat er met de betaalstand van de factuur gebeurde. */
  melding: string
}

export type FinqleBillingRequestFactuurKoppelInput = z.infer<
  typeof finqleBillingRequestFactuurKoppelSchema
>

// ─── Facturatiestatus-blok op de werkbon ─────────────────────────────────────

/** Eén regel in het facturatiestatus-blok op de werkbon-detailpagina. */
export interface FinqleWerkbonStatusRegel {
  /** Sleutel van de stap, voor iconen/volgorde in de UI. */
  stap: 'AANGELEVERD' | 'VERZOEK' | 'INGEDIEND' | 'AKKOORD' | 'AFGEKEURD' | 'GEFACTUREERD'
  label: string
  /** ISO-tijdstip, of null wanneer de stap nog niet bereikt is. */
  op: string | null
  toelichting: string | null
}

export interface FinqleWerkbonStatusDTO {
  werkbonId: string
  /** Facturatiemethode van de klant op dit moment. */
  methode: string
  /**
   * Loopt deze klant via de Finqle-API? Zo niet, dan is het statusblok niet
   * relevant en toont de UI het niet (behalve als er tóch al een verzoek is).
   */
  spiegeltFinqleApi: boolean
  regels: FinqleWerkbonStatusRegel[]
  billingRequestId: string | null
  billingRequestStatus: FinqleBillingRequestStatusKey | null
  /** batch-63 (FO-470) — herkomst van het getoonde verzoek. */
  billingRequestBron: FinqleVerzoekBronKey | null
  /** batch-63 (FO-470) — Finqle-factuurnummer zodra bekend (ook zonder factuurrij). */
  finqleInvoiceNumber: string | null
  invoiceId: string | null
  invoiceNummer: string | null
}

// ─── Beheer (instellingen -> Finqle-koppeling) ───────────────────────────────

/** SystemSetting-sleutel van de instelbare akkoord-drempel (dagen). */
export const FINQLE_DREMPEL_SETTING_KEY = 'finqle.akkoordDrempelDagen'

/** Verwerkstatussen van een ontvangen webhook-event (spiegel van de DB-enum). */
export const FINQLE_WEBHOOK_EVENT_STATUSSEN = [
  'ONTVANGEN',
  'VERWERKT',
  'GENEGEERD',
  'FOUT',
] as const

export type FinqleWebhookEventStatusKey = (typeof FINQLE_WEBHOOK_EVENT_STATUSSEN)[number]

export const FINQLE_WEBHOOK_EVENT_STATUS_LABELS: Record<FinqleWebhookEventStatusKey, string> = {
  ONTVANGEN: 'Ontvangen',
  VERWERKT: 'Verwerkt',
  GENEGEERD: 'Genegeerd',
  FOUT: 'Fout',
}

/** Status van één merchant-account op de beheerpagina. */
export interface FinqleAccountStatusDTO {
  account: FinqleAccountSlug
  label: string
  /** Is de API-key voor dit account als env aanwezig? */
  keyGeconfigureerd: boolean
  /** BV die dit account claimt (`Entity.finqleApiAccount`), of null. */
  bv: { id: string; naam: string } | null
  /** Laatste geslaagde sync-ronde van dit account (watermark), of null. */
  laatsteSyncOp: string | null
  alarm: {
    actief: boolean
    foutCode: string | null
    sindsIso: string | null
    mislukteRondes: number
  }
  verzoeken: { totaal: number; nietGekoppeld: number }
}

/** Status van de hele koppeling op de beheerpagina. */
export interface FinqleBeheerStatusDTO {
  productie: boolean
  /** GraphQL-endpoint (publieke URL, nooit een secret). */
  endpoint: string
  /** Hoe de webhook-afzender geverifieerd wordt. */
  webhookVerificatie: 'IP_ALLOWLIST' | 'SECRET'
  webhookIps: string[]
  /** Basis-pad van de webhook-URL's; per account komt `/<slug>` erachter. */
  webhookBasisUrl: string
  /** Zelfde naam als de PUT-input, zodat read en write niet uiteenlopen. */
  akkoordDrempelDagen: number
  /** Waar de drempel vandaan komt: de UI-instelling, env, of de standaard. */
  drempelBron: 'INSTELLING' | 'ENV' | 'STANDAARD'
  accounts: FinqleAccountStatusDTO[]
  /** Laatst ontvangen webhook-event (welk account dan ook), of null. */
  laatsteWebhookOp: string | null
}

/** Filter/paginering op de webhook-events-log. */
export const finqleWebhookEventsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  status: z.enum(FINQLE_WEBHOOK_EVENT_STATUSSEN).optional(),
  account: z.enum(FINQLE_ACCOUNTS).optional(),
})

export type FinqleWebhookEventsQuery = z.infer<typeof finqleWebhookEventsQuerySchema>

/** Rij in de webhook-events-log. */
export interface FinqleWebhookEventDTO {
  id: string
  account: string | null
  resourceType: string
  action: string
  objectId: string | null
  status: FinqleWebhookEventStatusKey
  statusLabel: string
  pogingen: number
  foutmelding: string | null
  bronIp: string | null
  ontvangenOp: string
  verwerktOp: string | null
}

/** Uitkomst van de verbinding-test per account. */
export interface FinqleVerbindingTestDTO {
  account: FinqleAccountSlug
  label: string
  ok: boolean
  melding: string | null
}

/**
 * Uitkomst van een (handmatige) sync-ronde over de beheer-API. Subset van het
 * interne sync-resultaat; gedeeld zodat backoffice en API niet stil uiteenlopen.
 */
export interface FinqleSyncRondeDTO {
  /** `true` = er is niets gedaan (geen enkele account-key ingericht). */
  overgeslagen: boolean
  verzoekenGezien: number
  verzoekenNieuw: number
  verzoekenNietGekoppeld: number
  facturenGezien: number
  facturenNieuw: number
  facturenNietGekoppeld: number
  /** Paginering-noodrem geraakt: er is werk blijven liggen voor de volgende ronde. */
  plafondGeraakt: boolean
  fout: string | null
  accounts: Array<{ account: FinqleAccountSlug; fout: string | null }>
}

/**
 * Antwoord van POST sync-nu: de afgeronde ronde, of `gestart` wanneer de ronde
 * langer duurt dan het request-venster en op de achtergrond doorloopt.
 */
export type FinqleSyncNuAntwoordDTO = { gestart: true } | ({ gestart: false } & FinqleSyncRondeDTO)

/** Instellingen die op de beheerpagina gewijzigd kunnen worden (ADMIN). */
export const finqleBeheerInstellingenSchema = z.object({
  akkoordDrempelDagen: z.number().int().min(1, 'Minimaal 1 dag').max(365, 'Maximaal 365 dagen'),
})

export type FinqleBeheerInstellingenInput = z.infer<typeof finqleBeheerInstellingenSchema>
